import type { JSX } from "react";
// Currency context.
//
// Source of truth for the supported list is the BE `currencies` table, fetched
// via GET /currencies. The user's selected display currency lives in
// localStorage `preferred_currency` (defaults to EUR).
//
// Conversion: rates are EUR-base; A→B = amount/rates[A] * rates[B].

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type Currency } from "../../api.js";

const STORAGE_KEY = "preferred_currency";
const DEFAULT_CURRENCY = "EUR";

interface RatesPayload {
  as_of: string | null;
  base: "EUR";
  rates: Record<string, number>;
}

interface CurrencyContextValue {
  /** Active currencies from the BE. Empty until first load. */
  currencies: Currency[];
  /** Selected display currency (always a `code` from `currencies`, or DEFAULT_CURRENCY). */
  selected: string;
  setSelected: (code: string) => void;
  rates: Record<string, number>;
  ratesReady: boolean;
  /** Convert an amount in minor units from `from` into the currently selected currency. */
  convert: (amountMinor: number, from: string) => number | null;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const API_BASE = import.meta.env.VITE_API_URL ?? "https://finance-api.kostanos.com";

function readStored(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }): JSX.Element {
  const [selected, setSelectedState] = useState<string>(readStored);

  const setSelected = useCallback((code: string) => {
    setSelectedState(code);
    try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  }, []);

  const list = useQuery({
    queryKey: ["currencies"],
    queryFn: () => api.list<Currency>("/currencies"),
    staleTime: 60 * 60 * 1000,
  });

  const rq = useQuery({
    queryKey: ["fx-rates"],
    queryFn: async (): Promise<RatesPayload> => {
      const res = await fetch(`${API_BASE}/fx/rates`, { credentials: "include" });
      if (!res.ok) throw new Error(`fx rates HTTP ${String(res.status)}`);
      return (await res.json()) as RatesPayload;
    },
    staleTime: 5 * 60 * 1000,
  });

  const currencies = list.data?.items ?? [];
  const rates = rq.data?.rates ?? { EUR: 1 };
  const ratesReady = rq.isSuccess;

  // If the persisted choice isn't in the active list once loaded, fall back.
  useEffect(() => {
    if (currencies.length === 0) return;
    if (!currencies.some((c) => c.code === selected)) {
      const fallback = currencies.some((c) => c.code === DEFAULT_CURRENCY) ? DEFAULT_CURRENCY : (currencies[0]?.code ?? DEFAULT_CURRENCY);
      setSelected(fallback);
    }
  }, [currencies, selected, setSelected]);

  const convert = useCallback(
    (amountMinor: number, from: string): number | null => {
      if (from === selected) return amountMinor;
      const rFrom = rates[from];
      const rTo = rates[selected];
      if (!rFrom || !rTo) return null;
      return Math.round((amountMinor / rFrom) * rTo);
    },
    [rates, selected],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({ currencies, selected, setSelected, rates, ratesReady, convert }),
    [currencies, selected, setSelected, rates, ratesReady, convert],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside <CurrencyProvider>");
  return ctx;
}

export function convertWithRates(
  amountMinor: number,
  from: string,
  to: string,
  rates: Record<string, number>,
): number | null {
  if (from === to) return amountMinor;
  const rFrom = rates[from];
  const rTo = rates[to];
  if (!rFrom || !rTo) return null;
  return Math.round((amountMinor / rFrom) * rTo);
}
